import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, GraduationCap, Loader2 } from "lucide-react";
import { FORMAT_META, fmtWhen, StateBadge } from "@/pages/events/eventUi";

export function TaskTraining({ taskId }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get(`/portal/tasks/${taskId}/training`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [taskId]);
  useEffect(() => { load(); }, [load]);
  if (!items.length) return null;
  return (
    <section data-testid="task-training-panel">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" />Available Training</h3>
      <div className="space-y-2">{items.map((ev) => <TrainingRow key={ev.id} ev={ev} onDone={load} />)}</div>
    </section>
  );
}

function TrainingRow({ ev, onDone }) {
  const [busy, setBusy] = useState(false);
  const F = FORMAT_META[ev.format] || FORMAT_META.online;
  const st = ev.my_registration?.status;
  const canReg = !["registered", "waitlisted"].includes(st) && ["open", "waitlist"].includes(ev.registration_state);
  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${ev.id}/register`, {});
      toast.success(data.registration.status === "waitlisted" ? "You're on the waitlist" : "You're registered");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`task-training-${ev.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-slate-900 text-sm">{ev.title}</p><StateBadge ev={ev} /></div>
        <p className="text-xs text-slate-600 mt-1 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{fmtWhen(ev)}</p>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5"><F.Icon className="w-3.5 h-3.5" />{F.label}{ev.seats_left != null ? ` · ${ev.seats_left} seats left` : ""}</p>
      </div>
      {canReg && (
        <Button size="sm" onClick={register} disabled={busy} data-testid={`task-training-register-${ev.id}`} className="rounded-full bg-[#006090] hover:bg-[#004E75] shrink-0">
          {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{ev.registration_state === "waitlist" ? "Join Waitlist" : "Register"}
        </Button>
      )}
    </div>
  );
}
